import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { LoadingSpinner } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { prospectsAPI, configuratorAPI, rentalCalcAPI, proposalV2API, pricingAPI } from '../utils/api';
import { RentalCalcSection } from '../components/RentalCalculator';
import DGDigitisationEditor from '../components/sales/DGDigitisationEditor';
import { Building2, MapPin, ArrowLeft, ClipboardList, Settings2, FileText,
  ChevronRight, CheckCircle, Lock, AlertTriangle, Circle,
  Upload, Trash2, Plus, Edit, Loader2, Play, Zap, Battery,
  Send, ShieldCheck, Copy, Link2, Fuel, Download, Eye, Save,
  FlaskConical
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { PreviewButton } from '../components/FilePreviewModal';
import SiteAnalysisTab from '../components/SiteAnalysisTab';

// ==================== STAGE RAIL ====================
const STAGES = [
  { id: 'requirement', label: 'Requirement', icon: ClipboardList },
  { id: 'analysis', label: 'Analysis', icon: FlaskConical },
  { id: 'configuration', label: 'Configuration', icon: Settings2 },
  { id: 'proposal', label: 'Proposal', icon: FileText },
];

const STAGE_STATUS = {
  draft: { icon: Circle, color: 'text-amber-600 bg-amber-50 border-amber-200', label: 'Draft' },
  submitted: { icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', label: 'Submitted' },
  active: { icon: Edit, color: 'text-indigo-600 bg-indigo-50 border-indigo-200', label: 'In Progress' },
  complete: { icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', label: 'Complete' },
  review: { icon: Eye, color: 'text-purple-600 bg-purple-50 border-purple-200', label: 'In Review' },
  approved: { icon: ShieldCheck, color: 'text-blue-600 bg-blue-50 border-blue-200', label: 'Approved' },
  sent: { icon: Send, color: 'text-emerald-700 bg-emerald-50 border-emerald-300', label: 'Sent' },
  frozen: { icon: Lock, color: 'text-slate-400 bg-slate-100 border-slate-200', label: 'Frozen' },
};

function getStageState(stage, site) {
  const req = site?.requirement_status || 'DRAFT';
  const cfg = site?.configuration_status || 'NOT_STARTED';
  const prop = site?.proposal_status || 'NOT_STARTED';
  const frozen = ['SENT', 'WON'].includes(prop);

  if (stage === 'requirement') {
    if (frozen) return { state: 'frozen', timestamp: null };
    if (req === 'SUBMITTED') return { state: 'submitted', timestamp: site.requirement_submitted_at };
    return { state: 'draft', timestamp: null };
  }
  if (stage === 'configuration') {
    if (frozen) return { state: 'frozen', timestamp: null };
    if (['LOCKED', 'SELECTED'].includes(cfg)) return { state: 'complete', timestamp: site.configuration_selected_at || site.configuration_locked_at };
    if (cfg === 'DRAFT') return { state: 'active', timestamp: null };
    return { state: 'draft', timestamp: null };
  }
  if (stage === 'analysis') {
    // Surfaces a green check once at least one analysis exists & is generated.
    if (site?.analysis_status === 'complete') return { state: 'complete', timestamp: site.analysis_generated_at };
    if (site?.analysis_status === 'confirmed') return { state: 'submitted', timestamp: site.analysis_confirmed_at };
    if (site?.analysis_status === 'review' || site?.analysis_status === 'extracting') return { state: 'active', timestamp: null };
    return { state: 'draft', timestamp: null };
  }
  if (stage === 'proposal') {
    if (prop === 'SENT') return { state: 'sent', timestamp: site.proposal_sent_at };
    if (prop === 'WON') return { state: 'complete', timestamp: null };
    if (prop === 'APPROVED') return { state: 'approved', timestamp: site.proposal_approved_at };
    if (prop === 'READY_FOR_REVIEW') return { state: 'review', timestamp: site.proposal_review_requested_at };
    if (prop === 'DRAFT') return { state: 'active', timestamp: null };
    return { state: 'draft', timestamp: null };
  }
  return { state: 'draft', timestamp: null };
}

function StageRail({ site, activeStage, onStageClick }) {
  return (
    <div className="flex items-center bg-white border border-slate-200 rounded-sm p-1.5 mb-6 gap-1" data-testid="stage-rail">
      {STAGES.map((stage, i) => {
        const { state, timestamp } = getStageState(stage.id, site);
        const cfg = STAGE_STATUS[state] || STAGE_STATUS.draft;
        const Icon = stage.icon;
        const StateIcon = cfg.icon;
        const isActive = activeStage === stage.id;

        return (
          <React.Fragment key={stage.id}>
            {i > 0 && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
            <button
              onClick={() => onStageClick(stage.id)}
              data-testid={`stage-${stage.id}`}
              className={`flex items-center space-x-2 px-4 py-2.5 rounded-sm text-sm font-medium transition-all flex-1
                ${isActive ? 'bg-slate-900 text-white shadow-sm' : `${cfg.color} border hover:opacity-80 cursor-pointer`}`}
            >
              <Icon className="w-4 h-4 shrink-0" strokeWidth={isActive ? 2 : 1.5} />
              <span className="font-heading">{stage.label}</span>
              {!isActive && <StateIcon className="w-3.5 h-3.5 ml-auto shrink-0" />}
              {timestamp && (
                <span className="font-mono text-[9px] ml-1 opacity-60">{new Date(timestamp).toLocaleDateString()}</span>
              )}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ==================== REQUIREMENT TAB ====================
function RequirementTab({ site, onRefresh, onGoToStage, token }) {
  const existingReq = site.requirements?.[0];
  const [form, setForm] = useState({
    use_case: existingReq?.use_case || 'backup',
    backup_hours_required: existingReq?.backup_hours_required ?? '',
    critical_load_kw: existingReq?.critical_load_kw ?? '',
    has_solar: existingReq?.has_solar || false,
    solar_pv_kwp: existingReq?.solar_pv_kwp ?? '',
    solar_data_available: existingReq?.solar_data_available || 'NONE',
    use_case_dg_replacement: existingReq?.use_case_dg_replacement || false,
    use_case_backup: existingReq?.use_case_backup || false,
    use_case_peak_shaving: existingReq?.use_case_peak_shaving || false,
    use_case_solar_shifting: existingReq?.use_case_solar_shifting || false,
    site_type: existingReq?.site_type || 'office',
    constraints: existingReq?.constraints || '',
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState(site.files || []);
  const [dgAssets, setDGAssets] = useState(site.dg_assets || []);
  const [showAddDG, setShowAddDG] = useState(false);
  const [dgForm, setDGForm] = useState({ dg_capacity_kva: '', dg_make: '', dg_model: '', dg_age_years: '', dg_fuel_type: 'diesel', dg_notes: '' });
  const fileRef = useRef(null);
  const [uploadType, setUploadType] = useState('electricity_bill');

  const proposalSent = ['SENT', 'WON'].includes(site.proposal_status);
  const isLocked = proposalSent;
  const siteId = site.prospect_site_id;
  const hasReq = !!existingReq;

  useEffect(() => {
    const r = site.requirements?.[0];
    setForm({
      use_case: r?.use_case || 'backup',
      backup_hours_required: r?.backup_hours_required ?? '',
      critical_load_kw: r?.critical_load_kw ?? '',
      has_solar: r?.has_solar || false,
      solar_pv_kwp: r?.solar_pv_kwp ?? '',
      solar_data_available: r?.solar_data_available || 'NONE',
      use_case_dg_replacement: r?.use_case_dg_replacement || false,
      use_case_backup: r?.use_case_backup || false,
      use_case_peak_shaving: r?.use_case_peak_shaving || false,
      use_case_solar_shifting: r?.use_case_solar_shifting || false,
      site_type: r?.site_type || 'office',
      constraints: r?.constraints || '',
    });
    setFiles(site.files || []);
    setDGAssets(site.dg_assets || []);
  }, [site]);

  // Derived energy (read-only)
  const derivedEnergy = (form.critical_load_kw && form.backup_hours_required)
    ? (Number(form.critical_load_kw) * Number(form.backup_hours_required)).toFixed(2)
    : null;

  // Validation
  const validateForm = () => {
    const newErrors = {};
    if (form.backup_hours_required !== '' && form.backup_hours_required !== null) {
      const bh = Number(form.backup_hours_required);
      if (isNaN(bh) || bh < 0.5 || bh > 6.0) newErrors.backup_hours = 'Must be between 0.5 and 6.0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save requirement (upsert — single record per site)
  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      await prospectsAPI.upsertRequirement(siteId, {
        ...form,
        backup_hours_required: form.backup_hours_required !== '' ? Number(form.backup_hours_required) : null,
        critical_load_kw: form.critical_load_kw !== '' ? Number(form.critical_load_kw) : null,
        solar_pv_kwp: form.solar_pv_kwp !== '' ? Number(form.solar_pv_kwp) : null,
      });
      toast.success('Requirement saved');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleAddDG = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await prospectsAPI.createDGAsset(siteId, {
        prospect_site_id: siteId, ...dgForm,
        dg_capacity_kva: dgForm.dg_capacity_kva ? Number(dgForm.dg_capacity_kva) : null,
        dg_age_years: dgForm.dg_age_years ? Number(dgForm.dg_age_years) : null,
      });
      toast.success('DG asset added');
      setShowAddDG(false);
      setDGForm({ dg_capacity_kva: '', dg_make: '', dg_model: '', dg_age_years: '', dg_fuel_type: 'diesel', dg_notes: '' });
      onRefresh();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await prospectsAPI.uploadFile(siteId, file, uploadType);
      toast.success('File uploaded');
      onRefresh();
    } catch { toast.error('Upload failed'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmitRequirement = async () => {
    if (!hasReq) { toast.error('Save requirement data first'); return; }
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      await prospectsAPI.submitRequirement(siteId);
      toast.success('Requirement submitted');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const completeness = site.data_completeness || {};

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {isLocked ? (
        <div className="bg-slate-100 border border-slate-300 rounded-sm p-4 flex items-center justify-between" data-testid="req-frozen-banner">
          <div className="flex items-center space-x-3">
            <Lock className="w-5 h-5 text-slate-500" />
            <div>
              <p className="text-sm font-semibold text-slate-700">Frozen — Proposal is SENT</p>
              <p className="text-xs text-slate-500">All edits are frozen after proposal is SENT.</p>
            </div>
          </div>
        </div>
      ) : site.requirement_status === 'SUBMITTED' ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 flex items-center justify-between" data-testid="req-submitted-banner">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="text-sm font-semibold text-emerald-700">Requirement Submitted</p>
              <p className="text-xs text-emerald-600">All fields remain editable. Changes are audit-logged.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="req-draft-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-sm font-semibold text-amber-700">Requirement in Draft</p>
                <p className="text-xs text-amber-600">Complete the form and upload documents, then submit.</p>
              </div>
            </div>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={handleSubmitRequirement} disabled={submitting || !hasReq} data-testid="submit-req-btn">
              <Send className="w-3 h-3 mr-1" /> Submit Requirement
            </Button>
          </div>
        </div>
      )}

      {/* Data Completeness */}
      <div className="bg-white border border-slate-200 rounded-sm p-4">
        <p className="section-label mb-3">Data Completeness</p>
        <div className="flex flex-wrap gap-3">
          {[
            { key: 'has_requirement', label: 'Requirement' },
            { key: 'has_use_case_flags', label: 'Use Case Flags' },
            { key: 'has_backup_requirement', label: 'Backup Hours' },
            { key: 'has_bills', label: 'Elec. Bills' },
            { key: 'has_site_details', label: 'Site Details' },
            ...(site.has_dg ? [{ key: 'has_dg_logs', label: 'DG Logs' }] : []),
            ...(site.requirements?.[0]?.has_solar ? [{ key: 'has_solar_data', label: 'Solar Data' }] : []),
          ].map(c => (
            <span key={c.key} className={`text-xs px-2.5 py-1 rounded-sm border font-medium ${completeness[c.key] ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
              {completeness[c.key] ? <CheckCircle className="w-3 h-3 inline mr-1" /> : <AlertTriangle className="w-3 h-3 inline mr-1" />}
              {c.label}
            </span>
          ))}
        </div>
      </div>

      {/* ===== PARALLEL BLOCKS ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* BLOCK 1: Structured Inputs */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="section-label mb-4">Asset Requirement</p>
            <fieldset disabled={isLocked} className="space-y-3">
              {/* Use Case Flags (multi-select) */}
              <div>
                <Label className="text-xs font-semibold text-slate-700 mb-2 block">Use Case Flags</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'use_case_backup', label: 'Backup' },
                    { key: 'use_case_dg_replacement', label: 'DG Replacement' },
                    { key: 'use_case_peak_shaving', label: 'Peak Shaving' },
                    { key: 'use_case_solar_shifting', label: 'Solar Shifting' },
                  ].map(uc => (
                    <label key={uc.key} className="flex items-center space-x-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={form[uc.key]} onChange={e => setForm({...form, [uc.key]: e.target.checked})}
                        className="rounded border-slate-300" data-testid={`req-${uc.key}`} />
                      <span>{uc.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Site Type */}
              <div>
                <Label className="text-xs">Site Type</Label>
                <select value={form.site_type} onChange={e => setForm({...form, site_type: e.target.value})}
                  className="w-full mt-1 text-sm border border-slate-200 rounded px-3 py-2 disabled:bg-slate-50 disabled:text-slate-500" data-testid="req-site-type">
                  <option value="office">Office</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="hospital">Hospital</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="education">Education</option>
                  <option value="retail_mall">Retail / Mall</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div>
                <Label className="text-xs">Critical Load (kW)</Label>
                <Input type="number" value={form.critical_load_kw} onChange={e => setForm({...form, critical_load_kw: e.target.value})}
                  className="mt-1" placeholder="e.g. 100" data-testid="req-critical-load" />
              </div>
              <div>
                <Label className="text-xs">Backup Hours Required</Label>
                <Input type="number" step="0.5" min="0.5" max="6.0" value={form.backup_hours_required}
                  onChange={e => { setForm({...form, backup_hours_required: e.target.value}); setErrors(prev => ({...prev, backup_hours: undefined})); }}
                  className={`mt-1 ${errors.backup_hours ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder="0.5 - 6.0 (step 0.5)" data-testid="req-backup-hours" />
                {errors.backup_hours && <p className="text-[11px] text-red-500 mt-1" data-testid="backup-hours-error">{errors.backup_hours}</p>}
              </div>
              {/* Derived Energy (read-only) */}
              <div>
                <Label className="text-xs text-slate-400">Derived Daily Energy (kWh) <span className="italic">= Critical Load x Backup Hours</span></Label>
                <Input type="text" value={derivedEnergy ? `${derivedEnergy} kWh` : '—'} readOnly
                  className="mt-1 bg-slate-50 text-slate-500 cursor-not-allowed" data-testid="req-derived-energy" />
              </div>

              {/* Solar Section */}
              <div className="border border-slate-100 rounded-sm p-3 space-y-2 bg-slate-50/50">
                <div className="flex items-center space-x-2">
                  <input type="checkbox" checked={form.has_solar} onChange={e => setForm({...form, has_solar: e.target.checked, ...(!e.target.checked ? { solar_pv_kwp: '', solar_data_available: 'NONE' } : {})})}
                    id="has_solar" className="rounded" data-testid="req-has-solar" />
                  <Label htmlFor="has_solar" className="text-xs font-semibold">Solar Available at Site</Label>
                </div>
                {form.has_solar && (
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div>
                      <Label className="text-xs">Solar PV Capacity (kWp)</Label>
                      <Input type="number" value={form.solar_pv_kwp} onChange={e => setForm({...form, solar_pv_kwp: e.target.value})}
                        className="mt-1" placeholder="e.g. 50" data-testid="req-solar-pv-kwp" />
                    </div>
                    <div>
                      <Label className="text-xs">Solar Data Available</Label>
                      <select value={form.solar_data_available} onChange={e => setForm({...form, solar_data_available: e.target.value})}
                        className="w-full mt-1 text-sm border border-slate-200 rounded px-3 py-2 disabled:bg-slate-50" data-testid="req-solar-data">
                        <option value="NONE">None</option>
                        <option value="MONTHLY">Monthly</option>
                        <option value="INTERVAL">Interval</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs">Constraints</Label>
                <Input value={form.constraints} onChange={e => setForm({...form, constraints: e.target.value})}
                  className="mt-1" placeholder="Space, noise, weight..." data-testid="req-constraints" />
              </div>
            </fieldset>
            {!isLocked && (
              <div className="mt-4 flex justify-end">
                <Button size="sm" onClick={handleSave} disabled={saving} className="bg-slate-900 text-xs" data-testid="save-req-btn">
                  {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                  {hasReq ? 'Update Requirement' : 'Save Requirement'}
                </Button>
              </div>
            )}
          </div>

          {/* DG Assets (within Block 1) */}
          {site.has_dg && (
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="section-label">DG Assets ({dgAssets.length})</p>
                {!isLocked && <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setShowAddDG(true)} data-testid="add-dg-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>}
              </div>
              {dgAssets.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No DG assets added</p>
              ) : (
                <div className="space-y-2">
                  {dgAssets.map(d => (
                    <div key={d.dg_asset_id} className="border border-slate-100 rounded-sm p-3 flex items-center justify-between text-xs">
                      <div className="flex items-center space-x-4">
                        <Fuel className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{d.dg_make} {d.dg_model}</span>
                        {d.dg_capacity_kva && <span className="font-mono text-slate-500">{d.dg_capacity_kva} kVA</span>}
                        {d.dg_age_years && <span className="font-mono text-slate-500">{d.dg_age_years} yrs</span>}
                      </div>
                      {!isLocked && (
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400 hover:text-red-600"
                          onClick={async () => { await prospectsAPI.deleteDGAsset(d.dg_asset_id); toast.success('Deleted'); onRefresh(); }}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* BLOCK 2: Document Uploads */}
        <div className="space-y-4">
          {/* DG Log Digitisation (if site has DG) - placed in Block 2 for visibility */}
          {site.has_dg && (
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <DGDigitisationEditor siteId={siteId} />
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="section-label">Document Vault ({files.length})</p>
              {!isLocked && (
                <div className="flex items-center space-x-2">
                  <select value={uploadType} onChange={e => setUploadType(e.target.value)}
                    className="text-xs border border-slate-200 rounded px-2 py-1" data-testid="upload-type-select">
                    <option value="electricity_bill">Electricity Bill</option>
                    <option value="dg_log">DG Log</option>
                    <option value="other">Other Document</option>
                  </select>
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => fileRef.current?.click()} data-testid="upload-file-btn">
                    <Upload className="w-3 h-3 mr-1" /> Upload
                  </Button>
                  <input ref={fileRef} type="file" className="hidden" onChange={handleFileUpload} />
                </div>
              )}
            </div>
            {files.length === 0 ? (
              <div className="py-8 text-center border-2 border-dashed border-slate-200 rounded-sm">
                <Upload className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No files uploaded yet</p>
                <p className="text-xs text-slate-300 mt-1">Upload electricity bills, DG logs, or other documents</p>
              </div>
            ) : (
              <div className="space-y-2">
                {files.map(f => {
                  const isDgLog = f.file_type === 'dg_log';
                  const extraction = f.extraction_result || {};
                  const dgRows = extraction.rows_inserted || 0;
                  const dgSummary = extraction.summary || {};
                  const dgOutliers = dgSummary.outlier_count || 0;
                  return (
                  <div key={f.upload_id} className="border border-slate-100 rounded-sm p-3 flex items-center justify-between text-xs" data-testid={`file-${f.upload_id}`}>
                    <div className="flex items-center space-x-3">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="font-medium text-slate-900">{f.original_filename}</p>
                        <p className="text-[10px] text-slate-400">{f.file_type?.replace('_', ' ')} · {f.parsing_status}</p>
                        {isDgLog && dgRows > 0 && (
                          <p className="text-[10px] text-emerald-600 mt-0.5" data-testid={`dg-extract-summary-${f.upload_id}`}>
                            Auto-parsed {dgRows} rows · {dgSummary.total_runtime_hours || 0} hrs · {dgSummary.total_diesel_litres || 0} L
                            {dgOutliers > 0 && <span className="text-amber-600 ml-1">· {dgOutliers} outliers</span>}
                          </p>
                        )}
                        {isDgLog && f.extraction_status === 'PROCESSING' && (
                          <p className="text-[10px] text-indigo-500 mt-0.5">Parsing DG log…</p>
                        )}
                        {isDgLog && f.extraction_status === 'error' && (
                          <p className="text-[10px] text-red-500 mt-0.5">Parse failed: {f.extraction_error || 'unsupported format'}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {f.storage_path && <PreviewButton uploadId={f.upload_id} filename={f.original_filename} token={token} size="xs" />}
                      {isDgLog && f.storage_path && (
                        <button
                          data-testid={`reparse-dg-${f.upload_id}`}
                          className="text-[10px] text-indigo-600 hover:underline"
                          onClick={async () => {
                            try {
                              const res = await prospectsAPI.parseDGLogFile(f.upload_id);
                              const inserted = res.data?.extraction_result?.rows_inserted || 0;
                              toast.success(`Re-parsed ${inserted} rows`);
                              onRefresh();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Re-parse failed');
                            }
                          }}
                        >
                          Re-parse
                        </button>
                      )}
                      {f.parsing_status === 'PENDING' || f.parsing_status === 'NEEDS_REVIEW' ? (
                        <a href={`/verification-queue?file=${f.upload_id}`} className="text-[10px] text-indigo-600 hover:underline" data-testid={`review-link-${f.upload_id}`}>
                          Review in Queue
                        </a>
                      ) : null}
                      {!isLocked && (
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400 hover:text-red-600"
                          onClick={async () => { await prospectsAPI.deleteFile(f.upload_id); toast.success('Deleted'); onRefresh(); }}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add DG Dialog */}
      <Dialog open={showAddDG} onOpenChange={setShowAddDG}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add DG Asset</DialogTitle></DialogHeader>
          <form onSubmit={handleAddDG} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Make</Label><Input value={dgForm.dg_make} onChange={e => setDGForm({...dgForm, dg_make: e.target.value})} className="mt-1" /></div>
              <div><Label className="text-xs">Model</Label><Input value={dgForm.dg_model} onChange={e => setDGForm({...dgForm, dg_model: e.target.value})} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Capacity (kVA)</Label><Input type="number" value={dgForm.dg_capacity_kva} onChange={e => setDGForm({...dgForm, dg_capacity_kva: e.target.value})} className="mt-1" /></div>
              <div><Label className="text-xs">Age (years)</Label><Input type="number" value={dgForm.dg_age_years} onChange={e => setDGForm({...dgForm, dg_age_years: e.target.value})} className="mt-1" /></div>
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <Button variant="outline" type="button" onClick={() => setShowAddDG(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900" disabled={saving}>{saving ? 'Saving...' : 'Add'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== CONFIGURATION TAB ====================
const MODE_LABELS = {
  SOLAR_BILLS_DG_DISPLACEMENT: { label: 'Solar + Bills + DG Displacement', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  BILLS_DG_DISPLACEMENT: { label: 'Bills + DG Displacement', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  BACKUP_SIZING: { label: 'Backup Sizing', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  SOLAR_SHIFTING: { label: 'Solar Shifting', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  BASIC_BESS_SIZING: { label: 'Basic BESS Sizing', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};

function MissingInputChecklist({ items, siteId, onGoToStage }) {
  if (!items || items.length === 0) return null;
  const hasMissing = items.some(i => i.status !== 'ok');
  if (!hasMissing) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-sm p-4" data-testid="missing-inputs-checklist">
      <p className="text-xs font-semibold text-amber-800 mb-2">Missing inputs for optimal sizing</p>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={item.input || i} className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-2">
              {item.status === 'ok' ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> :
               item.status === 'partial' ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> :
               <Circle className="w-3.5 h-3.5 text-red-400" />}
              <span className={item.status === 'ok' ? 'text-slate-600' : 'text-slate-800 font-medium'}>{item.input}</span>
            </div>
            <span className="text-[10px] text-slate-400">{item.why_needed}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onGoToStage?.('requirement')} data-testid="cta-upload-bills">
          <Upload className="w-3 h-3 mr-1" /> Upload Bills
        </Button>
        <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => onGoToStage?.('requirement')} data-testid="cta-upload-dg">
          <Upload className="w-3 h-3 mr-1" /> Upload DG Logs
        </Button>
      </div>
    </div>
  );
}

function ConfigurationTab({ site, onRefresh, onGoToStage }) {
  const [configRuns, setConfigRuns] = useState(site.config_runs || []);
  const [running, setRunning] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [deselecting, setDeselecting] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [activeMode, setActiveMode] = useState('system'); // system | vendor | manual
  const [vendorMatches, setVendorMatches] = useState(null);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [manualConfigs, setManualConfigs] = useState([]);
  const [manualForm, setManualForm] = useState({
    name: 'Manual Configuration', vendor: 'Custom',
    pcs_unit_kw: '', pcs_quantity: 1, battery_block_kwh: '', battery_quantity: 1,
    cooling_type: '', container_type: '', transformer_included: false, notes: '',
  });
  const [savingManual, setSavingManual] = useState(false);
  const { user } = useAuth();
  const siteId = site.prospect_site_id;
  const proposalFrozen = ['SENT', 'WON'].includes(site.proposal_status);
  const isConfigSelected = ['LOCKED', 'SELECTED'].includes(site.configuration_status);

  useEffect(() => { setConfigRuns(site.config_runs || []); }, [site]);

  const fetchDrafts = useCallback(async () => {
    try {
      const res = await prospectsAPI.listConfigDrafts(siteId);
      setDrafts(res.data || []);
    } catch (e) { console.error('fetchDrafts failed', e); }
  }, [siteId]);

  const fetchManualConfigs = useCallback(async () => {
    try {
      const res = await prospectsAPI.listManualConfigs(siteId);
      setManualConfigs(res.data || []);
    } catch (e) { console.error('fetchManualConfigs failed', e); }
  }, [siteId]);

  useEffect(() => { fetchDrafts(); fetchManualConfigs(); }, [siteId, fetchDrafts, fetchManualConfigs]);

  const handleSaveDraft = async (run, optionId) => {
    setSavingDraft(true);
    try {
      const opt = (run.outputs_json || []).find(o => o.option_id === optionId);
      await prospectsAPI.saveConfigDraft(siteId, {
        draft_name: opt ? `${opt.option_name} — ${run.mode || 'Config'}` : `Draft ${new Date().toLocaleDateString()}`,
        config_run_id: run.configurator_run_id,
        selected_option_id: optionId,
      });
      toast.success('Draft saved');
      fetchDrafts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save draft'); }
    finally { setSavingDraft(false); }
  };

  const handleDeleteDraft = async (draftId) => {
    try { await prospectsAPI.deleteConfigDraft(siteId, draftId); toast.success('Draft deleted'); fetchDrafts(); }
    catch { toast.error('Failed to delete draft'); }
  };

  const handleDuplicateDraft = async (draftId) => {
    try { await prospectsAPI.duplicateConfigDraft(siteId, draftId); toast.success('Draft duplicated'); fetchDrafts(); }
    catch { toast.error('Failed to duplicate draft'); }
  };

  const reqs = site.requirements || [];
  const latestReq = reqs[0];

  const handleRunConfigurator = async () => {
    if (!latestReq) { toast.error('No requirement found'); return; }
    setRunning(true);
    try {
      await configuratorAPI.run(latestReq.requirement_id);
      toast.success('Configurator run complete');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Configurator failed'); }
    finally { setRunning(false); }
  };

  const handleSelectConfig = async (run, optionId, mode = 'system') => {
    setSelecting(true);
    try {
      await prospectsAPI.selectConfiguration(siteId, {
        config_run_id: run.configurator_run_id,
        selected_option_id: optionId,
        mode,
      });
      toast.success('Configuration selected');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to select'); }
    finally { setSelecting(false); }
  };

  const handleSelectManual = async (mc) => {
    setSelecting(true);
    try {
      await prospectsAPI.selectConfiguration(siteId, {
        mode: 'manual',
        manual_config: mc,
      });
      toast.success('Manual configuration selected');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to select'); }
    finally { setSelecting(false); }
  };

  const handleDeselect = async () => {
    setDeselecting(true);
    try {
      await prospectsAPI.deselectConfiguration(siteId);
      toast.success('Configuration deselected');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to deselect'); }
    finally { setDeselecting(false); }
  };

  const handleRunVendorMatch = async () => {
    setVendorLoading(true);
    try {
      const res = await prospectsAPI.vendorMatch(siteId);
      setVendorMatches(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Vendor match failed'); }
    finally { setVendorLoading(false); }
  };

  const handleSaveManual = async () => {
    if (!manualForm.pcs_unit_kw || !manualForm.battery_block_kwh) {
      toast.error('PCS unit kW and battery block kWh are required');
      return;
    }
    setSavingManual(true);
    try {
      await prospectsAPI.saveManualConfig(siteId, {
        ...manualForm,
        pcs_unit_kw: Number(manualForm.pcs_unit_kw),
        pcs_quantity: Number(manualForm.pcs_quantity),
        battery_block_kwh: Number(manualForm.battery_block_kwh),
        battery_quantity: Number(manualForm.battery_quantity),
      });
      toast.success('Manual configuration saved');
      setManualForm({ name: 'Manual Configuration', vendor: 'Custom',
        pcs_unit_kw: '', pcs_quantity: 1, battery_block_kwh: '', battery_quantity: 1,
        cooling_type: '', container_type: '', transformer_included: false, notes: '' });
      fetchManualConfigs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSavingManual(false); }
  };

  // Show selected configuration snapshot (non-frozen)
  if (isConfigSelected && site.configuration_snapshot_json) {
    const snap = site.configuration_snapshot_json;
    const opt = snap.selected_option || {};
    const pricing = snap.pricing_match;
    return (
      <div className="space-y-4">
        {proposalFrozen ? (
          <div className="bg-slate-100 border border-slate-300 rounded-sm p-4 flex items-center space-x-3" data-testid="config-frozen-banner">
            <Lock className="w-5 h-5 text-slate-500" />
            <div>
              <p className="text-sm font-semibold text-slate-700">Frozen — Proposal is SENT</p>
              <p className="text-xs text-slate-500">All edits are frozen after proposal is SENT.</p>
            </div>
          </div>
        ) : (
          <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 flex items-center justify-between" data-testid="config-selected-banner">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <div>
                <p className="text-sm font-semibold text-emerald-700">Configuration Selected</p>
                <p className="text-xs text-emerald-600">
                  {snap.selection_mode === 'manual' ? 'Manual configuration' : `Selected from ${(snap.selection_mode || 'system').replace('_', ' ')} mode`}
                  {snap.selected_at && ` on ${new Date(snap.selected_at).toLocaleDateString()}`}. You can reselect anytime until proposal is SENT.
                </p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={handleDeselect} disabled={deselecting} className="text-xs" data-testid="deselect-config-btn">
              {deselecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Edit className="w-3 h-3 mr-1" />}
              Change Selection
            </Button>
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <p className="section-label mb-3">Selected Configuration</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
            <div><span className="text-slate-400">Option</span><p className="font-bold text-slate-900">{opt.option_name || opt.name}</p></div>
            <div><span className="text-slate-400">PCS Power</span><p className="font-mono font-medium text-slate-900">{opt.pcs_power_kw || opt.inverter_power_kw} kW</p></div>
            <div><span className="text-slate-400">Battery Energy</span><p className="font-mono font-medium text-slate-900">{opt.battery_energy_kwh} kWh</p></div>
            <div><span className="text-slate-400">Backup Hours</span><p className="font-mono font-medium text-slate-900">{opt.recommended_backup_hours || '—'} hrs</p></div>
            <div><span className="text-slate-400">Mode</span><p className="font-bold text-slate-900 capitalize">{(snap.selection_mode || opt.operating_mode || '').replace(/_/g, ' ')}</p></div>
            <div><span className="text-slate-400">Daily Discharge</span><p className="font-mono font-medium text-slate-900">{opt.expected_daily_discharge_kwh || '—'} kWh</p></div>
            <div><span className="text-slate-400">Confidence</span><p className="font-mono font-medium text-slate-900">{opt.confidence_score || '—'}%</p></div>
            {opt.vendor && <div><span className="text-slate-400">Vendor</span><p className="font-medium text-slate-900">{opt.vendor}</p></div>}
          </div>
        </div>
        {pricing && (
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="section-label mb-3">Matched Vendor Pricing</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div><span className="text-slate-400">PCS SKU</span><p className="font-medium text-slate-900">{pricing.pcs_sku}</p></div>
              <div><span className="text-slate-400">Battery SKU</span><p className="font-medium text-slate-900">{pricing.battery_sku}</p></div>
              <div><span className="text-slate-400">Total BESS Cost</span><p className="font-mono font-medium text-emerald-700">{pricing.total_bess_cost_INR ? `₹${Number(pricing.total_bess_cost_INR).toLocaleString('en-IN')}` : '—'}</p></div>
              <div><span className="text-slate-400">AMC/yr</span><p className="font-mono font-medium text-slate-900">{pricing.amc_annual_INR ? `₹${Number(pricing.amc_annual_INR).toLocaleString('en-IN')}` : '—'}</p></div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Latest run for mode/missing info
  const latestRun = configRuns[0];

  const MODE_TABS = [
    { key: 'system', label: 'System-Driven', icon: Play, desc: 'Auto-generated sizing options' },
    { key: 'vendor', label: 'Vendor Match', icon: Zap, desc: 'Closest vendor catalog matches' },
    { key: 'manual', label: 'Manual', icon: Edit, desc: 'User-defined configuration' },
  ];

  return (
    <div className="space-y-4">
      {/* Requirement Summary (inline) */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="section-label">Requirement Summary</p>
          <Button size="sm" variant="ghost" className="text-xs h-6" onClick={() => onGoToStage?.('requirement')}>
            <Edit className="w-3 h-3 mr-1" /> Edit Requirements
          </Button>
        </div>
        {latestReq ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><span className="text-slate-400">Use Cases</span>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {latestReq.use_case_backup && <span className="bg-blue-50 text-blue-700 text-[10px] px-1.5 py-0.5 rounded border border-blue-200">Backup</span>}
                {latestReq.use_case_dg_replacement && <span className="bg-amber-50 text-amber-700 text-[10px] px-1.5 py-0.5 rounded border border-amber-200">DG Replace</span>}
                {latestReq.use_case_peak_shaving && <span className="bg-violet-50 text-violet-700 text-[10px] px-1.5 py-0.5 rounded border border-violet-200">Peak Shave</span>}
                {latestReq.use_case_solar_shifting && <span className="bg-emerald-50 text-emerald-700 text-[10px] px-1.5 py-0.5 rounded border border-emerald-200">Solar Shift</span>}
              </div>
            </div>
            <div><span className="text-slate-400">Critical Load</span><p className="font-mono font-medium">{latestReq.critical_load_kw || '—'} kW</p></div>
            <div><span className="text-slate-400">Backup Hours</span><p className="font-mono font-medium">{latestReq.backup_hours_required || '—'} hrs</p></div>
            <div><span className="text-slate-400">Site Type</span><p className="font-medium capitalize">{(latestReq.site_type || 'office').replace(/_/g, ' ')}</p></div>
          </div>
        ) : (
          <p className="text-xs text-slate-400">No requirement data — <button className="text-indigo-600 underline" onClick={() => onGoToStage?.('requirement')}>add requirements first</button></p>
        )}
      </div>

      {/* 3-Mode Tabs */}
      <div className="flex border-b border-slate-200" data-testid="config-mode-tabs">
        {MODE_TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveMode(tab.key)} data-testid={`mode-tab-${tab.key}`}
            className={`flex items-center space-x-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors
              ${activeMode === tab.key ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
            <tab.icon className="w-3.5 h-3.5" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Mode A: System-Driven */}
      {activeMode === 'system' && (
        <div className="space-y-4">
          {latestRun?.mode && (
            <div className={`border rounded-sm p-3 flex items-center justify-between ${(MODE_LABELS[latestRun.mode] || MODE_LABELS.BASIC_BESS_SIZING).color}`} data-testid="mode-banner">
              <div>
                <p className="text-xs font-bold">Mode: {(MODE_LABELS[latestRun.mode] || { label: latestRun.mode }).label}</p>
                <p className="text-[10px] mt-0.5 opacity-80">{latestRun.mode_reason}</p>
              </div>
              <span className="font-mono text-xs font-bold">{latestRun.confidence_score ?? '—'}% confidence</span>
            </div>
          )}
          {latestRun?.missing_inputs && <MissingInputChecklist items={latestRun.missing_inputs} siteId={siteId} onGoToStage={onGoToStage} />}

          {/* Saved Drafts */}
          {drafts.length > 0 && (
            <div data-testid="config-drafts-section">
              <p className="section-label mb-2">Saved Drafts ({drafts.length})</p>
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-3 py-2 font-medium text-slate-500">Name</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-500 hidden sm:table-cell">Mode</th>
                    <th className="text-center px-3 py-2 font-medium text-slate-500">Confidence</th>
                    <th className="text-right px-3 py-2 font-medium text-slate-500">Actions</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {drafts.map(d => (
                      <tr key={d.draft_id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-800">{d.draft_name}</td>
                        <td className="px-3 py-2 text-slate-500 hidden sm:table-cell">{(d.mode || '').replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2 text-center"><span className="font-mono">{d.confidence_score ?? '—'}%</span></td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => handleDuplicateDraft(d.draft_id)}>Duplicate</Button>
                            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-red-500" onClick={() => handleDeleteDraft(d.draft_id)}>Delete</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Run Configurator */}
          <div className="flex items-center justify-between">
            <p className="section-label">Configurator Runs ({configRuns.length})</p>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs" onClick={handleRunConfigurator} disabled={running || proposalFrozen || !latestReq} data-testid="run-config-btn">
              {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
              Run Configurator
            </Button>
          </div>

          {configRuns.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
              <Settings2 className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No configurator runs yet</p>
              <p className="text-xs text-slate-400 mt-1">Click "Run Configurator" to generate sizing options</p>
            </div>
          ) : (
            configRuns.map(run => (
              <div key={run.configurator_run_id} className="bg-white border border-slate-200 rounded-sm p-4" data-testid={`config-run-${run.configurator_run_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3 text-xs text-slate-500">
                    <span>Run: {new Date(run.run_at).toLocaleString()}</span>
                    {run.mode && <span className={`px-2 py-0.5 rounded border text-[10px] font-bold ${(MODE_LABELS[run.mode] || MODE_LABELS.BASIC_BESS_SIZING).color}`}>{(MODE_LABELS[run.mode] || { label: run.mode }).label}</span>}
                  </div>
                </div>
                <div className="space-y-3">
                  {(run.outputs_json || []).map(opt => {
                    const isSelected = run.selected_option_id === opt.option_id;
                    const confScore = opt.confidence_score;
                    const confColor = confScore >= 70 ? 'text-emerald-600' : confScore >= 40 ? 'text-amber-600' : 'text-red-500';
                    const pricing = opt.indicative_pricing;
                    return (
                      <div key={opt.option_id} className={`border rounded-sm p-4 ${isSelected ? 'border-emerald-300 bg-emerald-50' : 'border-slate-100 hover:border-indigo-200'}`} data-testid={`opt-${opt.option_id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3 text-xs">
                            <span className="font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700">{opt.option_name}</span>
                            <span className="font-mono"><Zap className="w-3 h-3 inline mr-0.5" />{opt.pcs_power_kw || opt.inverter_power_kw} kW</span>
                            <span className="font-mono"><Battery className="w-3 h-3 inline mr-0.5" />{opt.battery_energy_kwh} kWh</span>
                            <span className="font-mono">{opt.recommended_backup_hours} hrs</span>
                            <span className={`font-mono font-semibold ${confColor}`}>{confScore}%</span>
                          </div>
                          {isSelected ? (
                            <span className="text-xs font-bold text-emerald-700">SELECTED</span>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <Button size="sm" variant="outline" className="text-xs h-7" disabled={savingDraft}
                                onClick={(e) => { e.stopPropagation(); handleSaveDraft(run, opt.option_id); }} data-testid={`save-draft-${opt.option_id}`}>
                                <Save className="w-3 h-3 mr-1" /> Draft
                              </Button>
                              <Button size="sm" className="text-xs h-7 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={selecting || proposalFrozen}
                                onClick={() => handleSelectConfig(run, opt.option_id, 'system')} data-testid={`select-config-${opt.option_id}`}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Select
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mb-2 text-[10px]">
                          <span className="bg-slate-50 px-2 py-0.5 rounded text-slate-600 font-medium">{(opt.operating_mode || '').replace(/_/g, ' ')}</span>
                          {opt.diesel_displaced_pct != null && (
                            <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-mono font-medium">~{opt.diesel_displaced_pct}% diesel displaced</span>
                          )}
                        </div>
                        {pricing && (
                          <div className="text-[10px] mb-2">
                            {pricing.source === 'pricing_matrix' ? (
                              <span className="font-mono text-emerald-600 font-medium">
                                Est. ₹{(pricing.total_bess_cost_inr / 100000).toFixed(1)}L
                                <span className="font-body text-slate-400 ml-1">({pricing.vendor} — {pricing.pcs_model})</span>
                              </span>
                            ) : (
                              <span className="text-amber-600">{pricing.message}</span>
                            )}
                          </div>
                        )}
                        {opt.confidence_breakdown && (
                          <details className="text-[10px] text-slate-500">
                            <summary className="cursor-pointer hover:text-slate-700 font-medium">Confidence ({confScore}%)</summary>
                            <div className="mt-1 grid grid-cols-2 gap-1 py-1">
                              {Object.entries(opt.confidence_breakdown).map(([k, v]) => (
                                <span key={k} className={v.status && v.points > 0 ? 'text-emerald-600' : 'text-red-400'}>
                                  {v.status && v.points > 0 ? '✓' : '✗'} {k.replace(/_/g, ' ')} {v.points !== 0 && <span className="font-mono">({v.points > 0 ? '+' : ''}{v.points})</span>}
                                </span>
                              ))}
                            </div>
                          </details>
                        )}
                        {opt.assumptions && (
                          <details className="text-[10px] text-slate-400 mt-1">
                            <summary className="cursor-pointer hover:text-slate-600 font-medium">Sizing assumptions</summary>
                            <div className="mt-1 grid grid-cols-2 gap-1 py-1">
                              {Object.entries(opt.assumptions).filter(([, v]) => v != null).map(([k, v]) => (
                                <span key={k} className="text-slate-500">{k.replace(/_/g, ' ')}: <span className="font-mono">{typeof v === 'number' ? v : String(v)}</span></span>
                              ))}
                            </div>
                          </details>
                        )}
                        {(opt.pcs_sizing || opt.battery_sizing) && (
                          <details className="text-[10px] text-slate-400 mt-1" data-testid={`debug-panel-${opt.option_id}`}>
                            <summary className="cursor-pointer hover:text-slate-600 font-medium text-indigo-600">Debug</summary>
                            <div className="mt-1 bg-indigo-50 border border-indigo-100 rounded p-2 space-y-1">
                              {opt.pcs_sizing && <div className="grid grid-cols-4 gap-1"><span>PCS: {opt.pcs_sizing.pcs_unit_kw}kW x{opt.pcs_sizing.pcs_count} = {opt.pcs_sizing.total_pcs_kw}kW</span><span>Oversize: {opt.pcs_sizing.pcs_oversize_pct}%</span></div>}
                              {opt.battery_sizing && <div className="grid grid-cols-4 gap-1"><span>Battery: {opt.battery_sizing.block_kwh}kWh x{opt.battery_sizing.block_count} = {opt.battery_sizing.total_battery_kwh}kWh</span><span>Oversize: {opt.battery_sizing.battery_oversize_pct}%</span></div>}
                            </div>
                          </details>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Mode B: Vendor Match */}
      {activeMode === 'vendor' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="section-label">Vendor Catalog Match</p>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs" onClick={handleRunVendorMatch} disabled={vendorLoading || proposalFrozen || !latestReq} data-testid="run-vendor-match-btn">
              {vendorLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
              Find Vendor Matches
            </Button>
          </div>

          {!latestReq && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 text-center text-xs text-amber-700">
              Add requirements first to find vendor matches.
              <Button size="sm" variant="link" className="text-xs ml-1 text-indigo-600" onClick={() => onGoToStage?.('requirement')}>Go to Requirements</Button>
            </div>
          )}

          {vendorMatches && (
            <div>
              <p className="text-xs text-slate-500 mb-2">
                Required: <span className="font-mono font-medium">{vendorMatches.required_pcs_kw} kW</span> PCS / <span className="font-mono font-medium">{vendorMatches.required_battery_kwh} kWh</span> battery — <span className="font-bold">{vendorMatches.total_matches} matches</span>
              </p>
              {vendorMatches.matches?.length > 0 ? (
                <div className="space-y-2">
                  {vendorMatches.matches.map(m => (
                    <div key={m.option_id} className="bg-white border border-slate-200 rounded-sm p-4 hover:border-indigo-200" data-testid={`vendor-match-${m.option_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3 text-xs">
                          <span className="font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">{m.vendor_name}</span>
                          <span className="font-mono">{m.pcs_sku}</span>
                          <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${m.meets_requirement ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                            Fit: {m.fit_score}%
                          </span>
                        </div>
                        <Button size="sm" className="text-xs h-7 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={selecting || proposalFrozen}
                          onClick={() => {
                            setSelecting(true);
                            prospectsAPI.selectConfiguration(siteId, {
                              mode: 'vendor_match',
                              manual_config: {
                                name: `${m.vendor_name} — ${m.pcs_sku}`,
                                vendor: m.vendor_name,
                                pcs_unit_kw: m.pcs_unit_kw,
                                pcs_quantity: m.pcs_count,
                                battery_block_kwh: m.battery?.block_kwh || 0,
                                battery_quantity: m.battery?.block_count || 1,
                              },
                            }).then(() => { toast.success('Vendor match selected'); onRefresh(); })
                              .catch(e => toast.error(e.response?.data?.detail || 'Failed'))
                              .finally(() => setSelecting(false));
                          }} data-testid={`select-vendor-${m.option_id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Select
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs text-slate-600">
                        <div><span className="text-slate-400">PCS</span><p className="font-mono">{m.pcs_unit_kw}kW x{m.pcs_count} = {m.total_pcs_kw}kW</p></div>
                        <div><span className="text-slate-400">PCS Oversize</span><p className="font-mono">{m.pcs_oversize_pct}%</p></div>
                        <div><span className="text-slate-400">Battery</span><p className="font-mono">{m.battery?.block_kwh}kWh x{m.battery?.block_count} = {m.total_battery_kwh}kWh</p></div>
                        <div><span className="text-slate-400">Battery Oversize</span><p className="font-mono">{m.battery?.battery_oversize_pct}%</p></div>
                        {m.estimated_cost_inr && <div><span className="text-slate-400">Est. Cost</span><p className="font-mono text-emerald-600">₹{(m.estimated_cost_inr / 100000).toFixed(1)}L</p></div>}
                      </div>
                      {m.system_sku && <p className="text-[10px] text-slate-400 mt-1">System: {m.system_sku} — {m.system_name}</p>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white border border-slate-200 rounded-sm p-6 text-center text-xs text-slate-400">No vendor matches found</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Mode C: Manual Configuration */}
      {activeMode === 'manual' && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <p className="section-label mb-3">New Manual Configuration</p>
            <fieldset disabled={proposalFrozen}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                <div>
                  <Label className="text-[10px]">Config Name</Label>
                  <Input value={manualForm.name} onChange={e => setManualForm(p => ({...p, name: e.target.value}))} className="h-8 text-xs" />
                </div>
                <div>
                  <Label className="text-[10px]">Vendor</Label>
                  <Input value={manualForm.vendor} onChange={e => setManualForm(p => ({...p, vendor: e.target.value}))} className="h-8 text-xs" placeholder="Custom" />
                </div>
                <div>
                  <Label className="text-[10px]">PCS Unit Rating (kW)</Label>
                  <Input type="number" value={manualForm.pcs_unit_kw} onChange={e => setManualForm(p => ({...p, pcs_unit_kw: e.target.value}))} className="h-8 text-xs" data-testid="manual-pcs-kw" />
                </div>
                <div>
                  <Label className="text-[10px]">PCS Quantity</Label>
                  <Input type="number" min={1} value={manualForm.pcs_quantity} onChange={e => setManualForm(p => ({...p, pcs_quantity: e.target.value}))} className="h-8 text-xs" data-testid="manual-pcs-qty" />
                </div>
                <div>
                  <Label className="text-[10px]">Battery Block (kWh)</Label>
                  <Input type="number" value={manualForm.battery_block_kwh} onChange={e => setManualForm(p => ({...p, battery_block_kwh: e.target.value}))} className="h-8 text-xs" data-testid="manual-bat-kwh" />
                </div>
                <div>
                  <Label className="text-[10px]">Battery Quantity</Label>
                  <Input type="number" min={1} value={manualForm.battery_quantity} onChange={e => setManualForm(p => ({...p, battery_quantity: e.target.value}))} className="h-8 text-xs" data-testid="manual-bat-qty" />
                </div>
                <div>
                  <Label className="text-[10px]">Cooling Type</Label>
                  <Input value={manualForm.cooling_type} onChange={e => setManualForm(p => ({...p, cooling_type: e.target.value}))} className="h-8 text-xs" placeholder="Air / Liquid" />
                </div>
                <div>
                  <Label className="text-[10px]">Container Type</Label>
                  <Input value={manualForm.container_type} onChange={e => setManualForm(p => ({...p, container_type: e.target.value}))} className="h-8 text-xs" placeholder="20ft / 40ft" />
                </div>
                <div className="flex items-end gap-2">
                  <label className="flex items-center space-x-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={manualForm.transformer_included} onChange={e => setManualForm(p => ({...p, transformer_included: e.target.checked}))} className="rounded" />
                    <span>Transformer included</span>
                  </label>
                </div>
              </div>
              {manualForm.pcs_unit_kw && manualForm.battery_block_kwh && (
                <div className="mt-3 bg-indigo-50 border border-indigo-100 rounded-sm p-2 text-xs text-indigo-700 font-mono">
                  Total: {(Number(manualForm.pcs_unit_kw) * Number(manualForm.pcs_quantity || 1)).toFixed(1)} kW PCS / {(Number(manualForm.battery_block_kwh) * Number(manualForm.battery_quantity || 1)).toFixed(1)} kWh Battery
                </div>
              )}
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" variant="outline" className="text-xs" onClick={handleSaveManual} disabled={savingManual} data-testid="save-manual-config-btn">
                  {savingManual ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save Draft
                </Button>
                <Button size="sm" className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white" disabled={selecting || !manualForm.pcs_unit_kw || !manualForm.battery_block_kwh}
                  onClick={() => handleSelectManual({
                    name: manualForm.name, vendor: manualForm.vendor,
                    pcs_unit_kw: Number(manualForm.pcs_unit_kw), pcs_quantity: Number(manualForm.pcs_quantity || 1),
                    battery_block_kwh: Number(manualForm.battery_block_kwh), battery_quantity: Number(manualForm.battery_quantity || 1),
                    cooling_type: manualForm.cooling_type, container_type: manualForm.container_type,
                    transformer_included: manualForm.transformer_included, notes: manualForm.notes,
                  })} data-testid="select-manual-config-btn">
                  <CheckCircle className="w-3 h-3 mr-1" /> Save & Select
                </Button>
              </div>
            </fieldset>
          </div>

          {/* Saved Manual Configs */}
          {manualConfigs.length > 0 && (
            <div>
              <p className="section-label mb-2">Saved Manual Configurations ({manualConfigs.length})</p>
              <div className="space-y-2">
                {manualConfigs.map(mc => (
                  <div key={mc.manual_config_id} className="bg-white border border-slate-200 rounded-sm p-3 flex items-center justify-between hover:border-indigo-200" data-testid={`manual-config-${mc.manual_config_id}`}>
                    <div className="text-xs space-y-1">
                      <p className="font-medium text-slate-800">{mc.name} <span className="text-slate-400">({mc.vendor})</span></p>
                      <p className="font-mono text-slate-600">{mc.total_pcs_kw} kW PCS / {mc.total_battery_kwh} kWh Battery</p>
                    </div>
                    <Button size="sm" className="text-xs h-7 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={selecting || proposalFrozen}
                      onClick={() => handleSelectManual(mc)} data-testid={`select-saved-manual-${mc.manual_config_id}`}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Select
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// V2 template options — matches /app/backend/template_library
const V2_TEMPLATE_OPTIONS = [
  { key: 'voltide.sale_only.v1', label: 'Sale Only', desc: 'Outright purchase (CapEx)' },
  { key: 'voltide.sale_or_rental.v1', label: 'Sale + Rental', desc: 'Dual option — client chooses' },
  { key: 'voltide.rental_only.v1', label: 'Rental Only', desc: 'Pay-per-kWh BaaS (OpEx)' },
];

// ==================== PROPOSAL TAB (V2 engine) ====================
function ProposalTab({ site, onRefresh, onGoToStage }) {
  const siteId = site.prospect_site_id;
  const hasConfig = ['LOCKED', 'SELECTED'].includes(site.configuration_status);
  const proposalFrozen = ['SENT', 'WON'].includes(site.proposal_status);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('voltide.rental_only.v1');
  const [requesting, setRequesting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [sending, setSending] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const isManagement = ['management', 'admin', 'super_admin'].includes(user?.role);
  const [rentalCalcs, setRentalCalcs] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, rcRes] = await Promise.all([
          proposalV2API.list(),
          prospectsAPI.getSiteRentalCalcs ? prospectsAPI.getSiteRentalCalcs(siteId).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);
        // Filter V2 proposals that originated from this prospect site
        setProposals((pRes.data || []).filter(p => p.source_site_id === siteId));
        setRentalCalcs(rcRes.data || []);
      } catch (e) { console.error('ProspectSiteDetailPage load failed', e); }
      finally { setLoading(false); }
    };
    load();
  }, [siteId]);

  const handleCreateAndPopulate = async () => {
    setCreating(true);
    try {
      const res = await proposalV2API.createFromSite(selectedTemplate, siteId);
      toast.success('Proposal auto-generated from site data');
      onRefresh();
      // Navigate directly into the new V2 editor
      navigate(`/proposals/${res.data.proposal_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create proposal');
    } finally {
      setCreating(false);
    }
  };

  const handleRequestApproval = async () => {
    setRequesting(true);
    try {
      await prospectsAPI.requestApproval(siteId);
      toast.success('Approval requested');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setRequesting(false); }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await prospectsAPI.approveProposal(siteId, approvalNotes);
      toast.success('Proposal approved');
      setApprovalNotes('');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setApproving(false); }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await prospectsAPI.sendProposal(siteId);
      toast.success('Proposal sent — all edits are now frozen');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const snap = site.configuration_snapshot_json || {};
  const opt = snap.selected_option || {};
  const pricing = snap.pricing_match;
  const propStatus = site.proposal_status || 'NOT_STARTED';
  const latestProposal = proposals.length > 0 ? proposals[0] : null;
  const hasRentalCalc = rentalCalcs.length > 0;

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Frozen Banner */}
      {proposalFrozen && (
        <div className="bg-slate-100 border border-slate-300 rounded-sm p-4 flex items-center space-x-3" data-testid="proposal-frozen-banner">
          <Lock className="w-5 h-5 text-slate-500" />
          <div>
            <p className="text-sm font-semibold text-slate-700">Frozen — Proposal is {propStatus}</p>
            <p className="text-xs text-slate-500">All edits are frozen after proposal is SENT.</p>
          </div>
        </div>
      )}

      {/* ============ STEP 1: Configuration Summary ============ */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center space-x-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700">1</div>
          <p className="text-sm font-semibold text-slate-900">Configuration</p>
          {hasConfig && <CheckCircle className="w-4 h-4 text-emerald-500" />}
        </div>
        {hasConfig ? (
          <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-4">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-indigo-600">
              <span>{opt.option_name || opt.name}: <b className="font-mono">{opt.pcs_power_kw || opt.inverter_power_kw} kW / {opt.battery_energy_kwh} kWh</b></span>
              {pricing && <span>SKU: <b>{pricing.pcs_sku} x {pricing.battery_sku}</b></span>}
              {pricing?.total_bess_cost_INR && <span>Cost: <b className="font-mono">₹{Number(pricing.total_bess_cost_INR).toLocaleString('en-IN')}</b></span>}
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex items-center justify-between">
            <p className="text-xs text-amber-700">No configuration selected yet. Select one first for accurate pricing.</p>
            <Button size="sm" variant="outline" className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => onGoToStage?.('configuration')} data-testid="go-to-config-btn">
              <Settings2 className="w-3 h-3 mr-1" /> Go to Configuration
            </Button>
          </div>
        )}
      </div>

      {/* ============ STEP 2: Rental Calculator ============ */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center space-x-2 mb-3">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${hasConfig ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'}`}>2</div>
          <p className="text-sm font-semibold text-slate-900">Rental Calculation</p>
          {hasRentalCalc && <CheckCircle className="w-4 h-4 text-emerald-500" />}
        </div>
        {hasConfig ? (
          <RentalCalcSection siteId={siteId} configSnapshot={site.configuration_snapshot_json} />
        ) : (
          <p className="text-xs text-slate-400 py-2">Complete configuration first to run rental calculations.</p>
        )}
      </div>

      {/* ============ STEP 3: Proposal ============ */}
      <div className="bg-white border border-slate-200 rounded-sm p-5">
        <div className="flex items-center space-x-2 mb-3">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${hasConfig ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'}`}>3</div>
          <p className="text-sm font-semibold text-slate-900">Proposal</p>
          {latestProposal && <CheckCircle className="w-4 h-4 text-emerald-500" />}
        </div>

        {/* No proposal yet — show create CTA */}
        {!latestProposal && (
          <div className="border-2 border-dashed border-slate-200 rounded-sm p-6 text-center" data-testid="create-proposal-cta">
            <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-600 mb-1">No proposal created yet</p>
            <p className="text-xs text-slate-400 mb-4">Select a template — we'll auto-fill customer, system sizing, and diesel log data from this site.</p>
            <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
              {V2_TEMPLATE_OPTIONS.map(t => (
                <button key={t.key} onClick={() => setSelectedTemplate(t.key)}
                  data-testid={`template-${t.key}`}
                  className={`px-4 py-3 rounded-sm border text-left transition-all ${selectedTemplate === t.key
                    ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                    : 'border-slate-200 bg-white hover:border-indigo-200'}`}>
                  <p className={`text-xs font-semibold ${selectedTemplate === t.key ? 'text-indigo-700' : 'text-slate-700'}`}>{t.label}</p>
                  <p className="text-[10px] text-slate-400">{t.desc}</p>
                </button>
              ))}
            </div>
            <Button onClick={handleCreateAndPopulate} disabled={creating || !hasConfig} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-populate-proposal-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              {creating ? 'Creating...' : 'Create & Auto-Populate Proposal'}
            </Button>
            {!hasConfig && <p className="text-[10px] text-amber-600 mt-2">Select a configuration first to enable proposal creation.</p>}
          </div>
        )}

        {/* Proposal exists — inline summary + editor link */}
        {latestProposal && (
          <div className="space-y-3">
            {/* Inline Preview Card */}
            <div className="border border-slate-200 rounded-sm p-4 hover:border-indigo-200 transition-colors" data-testid="proposal-preview-card">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                      latestProposal.status === 'draft' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
                    }`}>{latestProposal.status}</span>
                    <span className="text-xs text-slate-500">{latestProposal.template_id?.split('.')[1]?.replace(/_/g, ' ')}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-900">{latestProposal.payload?.customer?.name || `Proposal for ${site.site_name}`}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Created {new Date(latestProposal.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => navigate(`/proposals/${latestProposal.proposal_id}`)} data-testid="open-proposal-editor-btn">
                    <Edit className="w-3 h-3 mr-1" /> Open Editor
                  </Button>
                </div>
              </div>
            </div>

            {/* Additional proposals */}
            {proposals.length > 1 && (
              <div className="space-y-1">
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Other Proposals ({proposals.length - 1})</p>
                {proposals.slice(1).map(p => (
                  <div key={p.proposal_id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded text-xs cursor-pointer hover:bg-slate-100"
                    onClick={() => navigate(`/proposals/${p.proposal_id}`)}>
                    <div className="flex items-center space-x-2">
                      <span className={`font-bold px-1.5 py-0.5 rounded uppercase ${p.status === 'draft' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{p.status}</span>
                      <span className="text-slate-600">{p.template_id?.split('.')[1]?.replace(/_/g, ' ')}</span>
                    </div>
                    <ChevronRight className="w-3 h-3 text-slate-300" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============ STEP 4: Approval & Send ============ */}
      {latestProposal && !proposalFrozen && (
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="proposal-approval-section">
          <div className="flex items-center space-x-2 mb-3">
            <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700">4</div>
            <p className="text-sm font-semibold text-slate-900">Approval & Send</p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-3 mb-4">
            {['DRAFT', 'READY_FOR_REVIEW', 'APPROVED', 'SENT'].map((s, i) => {
              const isCurrent = propStatus === s || (s === 'DRAFT' && propStatus === 'NOT_STARTED');
              const isPast = ['DRAFT', 'READY_FOR_REVIEW', 'APPROVED', 'SENT'].indexOf(propStatus === 'NOT_STARTED' ? 'DRAFT' : propStatus) > i;
              return (
                <div key={s} className={`flex items-center text-xs font-medium px-3 py-1.5 rounded-sm border
                  ${isCurrent ? 'bg-indigo-600 text-white border-indigo-600' : isPast ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
                  {isPast && <CheckCircle className="w-3 h-3 mr-1" />}
                  {s.replace(/_/g, ' ')}
                </div>
              );
            })}
          </div>

          {/* Smart Action CTA */}
          <div className="flex items-center gap-2 flex-wrap">
            {(propStatus === 'NOT_STARTED' || propStatus === 'DRAFT') && (
              <Button size="sm" className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleRequestApproval} disabled={requesting} data-testid="request-approval-btn">
                {requesting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Eye className="w-3 h-3 mr-1" />}
                Request Approval
              </Button>
            )}
            {propStatus === 'READY_FOR_REVIEW' && !isManagement && (
              <span className="text-xs text-purple-600 font-medium bg-purple-50 px-3 py-1.5 rounded-sm">Awaiting management approval...</span>
            )}
            {isManagement && propStatus === 'READY_FOR_REVIEW' && (
              <div className="flex items-center gap-2">
                <Input value={approvalNotes} onChange={e => setApprovalNotes(e.target.value)} placeholder="Approval notes (optional)" className="h-8 text-xs w-48" />
                <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={handleApprove} disabled={approving} data-testid="approve-proposal-btn">
                  {approving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                  Approve
                </Button>
              </div>
            )}
            {(propStatus === 'APPROVED' || propStatus === 'READY_FOR_REVIEW') && (
              <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleSend} disabled={sending} data-testid="send-proposal-btn">
                {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                Send Proposal
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function ProspectSiteDetailPage() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const activeStage = searchParams.get('stage') || 'requirement';
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await prospectsAPI.getPipelineStatus(siteId);
      setSite(res.data);
    } catch (e) { toast.error('Failed to load site'); }
    finally { setLoading(false); }
  }, [siteId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setStage = (s) => setSearchParams({ stage: s });

  if (loading) return <div className="flex"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0"><LoadingSpinner /></div></div>;
  if (!site) return <div className="flex"><Sidebar /><div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0 p-8"><p className="text-slate-500">Site not found</p></div></div>;

  const breadcrumbs = [
    { label: 'Prospects', to: '/prospects' },
    { label: site.prospect_company_name, to: `/prospects/${site.prospect_id}` },
    { label: site.site_name },
  ];

  const nextAction = site.next_action;
  const stage = site.pipeline_stage;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Breadcrumbs items={breadcrumbs} onBack={() => navigate(`/prospects/${site.prospect_id}`)} />

          {/* Header */}
          <div className="bg-white border border-slate-200 rounded-sm p-5 shadow-sm mb-6" data-testid="site-header">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-indigo-50 border border-indigo-200 rounded-sm flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h1 className="font-heading text-lg lg:text-xl font-semibold tracking-tight text-slate-900">{site.site_name}</h1>
                  <p className="text-xs text-slate-500 flex items-center space-x-2">
                    <MapPin className="w-3 h-3" />
                    <span>{[site.city, site.state].filter(Boolean).join(', ') || 'No location'}</span>
                    {site.has_dg && <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded text-[10px] font-bold border border-amber-200">DG</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`text-[10px] font-bold px-2 py-1 rounded border ${stage === 'WON' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : stage === 'LOST' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'}`}>
                  {stage?.replace(/_/g, ' ')}
                </span>
              </div>
            </div>
            {nextAction && (
              <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-sm px-3 py-2 flex items-center space-x-2 text-xs" data-testid="next-action-cta">
                <ChevronRight className="w-3 h-3 text-indigo-600" />
                <span className="text-indigo-700 font-medium">Next: {nextAction}</span>
              </div>
            )}
          </div>

          {/* Stage Rail */}
          <StageRail site={site} activeStage={activeStage} onStageClick={setStage} />

          {/* Stage Content */}
          {activeStage === 'requirement' && <RequirementTab site={site} onRefresh={fetchData} onGoToStage={setStage} token={token} />}
          {activeStage === 'configuration' && <ConfigurationTab site={site} onRefresh={fetchData} onGoToStage={setStage} />}
          {activeStage === 'analysis' && <SiteAnalysisTab site={site} />}
          {activeStage === 'proposal' && <ProposalTab site={site} onRefresh={fetchData} onGoToStage={setStage} />}
        </div>
      </div>
    </div>
  );
}
